import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import SlidePanel from '../shared/SlidePanel';
import VoucherCard from './VoucherCard';
import { buildPreviewVoucher } from '../giftcard-shop/shopUtils';
import { isSaleUnclaimed } from './data';

// SalesDetailPanel — per-sale slide-out shown when the salon clicks a
// row in the Sales table.
//
// Designed so the POS counter use case lives at the top: the voucher
// code is the FIRST thing visible (large monospace + copy button), so
// staff can read it out to a customer or write it on a physical card
// without scrolling.
//
// Action buttons at the bottom are stubs in v1 — backend hookup later.

const STATUS_CONFIG = {
  active:   { label: 'Active',   bg: '#d1fae5', color: '#059669' },
  redeemed: { label: 'Redeemed', bg: '#f3f4f6', color: '#374151' },
  partial:  { label: 'Partial',  bg: '#fef3c7', color: '#b45309' },
  expired:  { label: 'Expired',  bg: '#fee2e2', color: '#dc2626' },
};

const fmtDateTime = (iso) => {
  if (!iso) return 'None';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};
const fmtDate = (iso) => {
  if (!iso) return 'None';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="4" y="4" width="8" height="8" rx="1" />
    <path d="M10 4V3a1 1 0 00-1-1H3a1 1 0 00-1 1v6a1 1 0 001 1h1" />
  </svg>
);
const PrintIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 5V1h8v4M3 11H2a1 1 0 01-1-1V6a1 1 0 011-1h10a1 1 0 011 1v4a1 1 0 01-1 1h-1M3 9h8v4H3z" />
  </svg>
);
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
    <path d="M7 1v12M1 7h12" />
  </svg>
);

const SalesDetailPanel = ({ sale, voucher, design, salonName = '', currency = '£', onClose, onResendEmail, onAttachClient }) => {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(null); // 'resend' | null
  // Attach-client inline form: which role is being attached + the entered contact.
  const [attachRole, setAttachRole] = useState(null); // 'buyer' | 'recipient' | null
  const [attachName, setAttachName] = useState('');
  const [attachEmail, setAttachEmail] = useState('');
  const [attaching, setAttaching] = useState(false);

  if (!sale) return null;

  const status = STATUS_CONFIG[sale.status] || STATUS_CONFIG.active;
  const unclaimed = isSaleUnclaimed(sale);
  const hasGift = !!sale.recipientName || !!sale.recipientEmail;
  const promoActive = sale.paidAmount < sale.faceValue;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(sale.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // Fallback: log so QA can still read the code from devtools
      console.log('[SalesDetail] Voucher code:', sale.code);
    }
  };

  // Stubs — actions without a backend yet (PDF, attach client, print) land a
  // console breadcrumb. Mark-redeemed and resend-email are wired below.
  // Print / Download PDF — renders the designed card + full sale details into
  // a print-only portal and opens the browser's print dialog (Save as PDF).
  // Same zero-dependency approach as the customer confirmation page.
  const printVoucher = () => {
    // Give the portal a paint frame before the dialog freezes the page.
    setTimeout(() => window.print(), 60);
  };

  const stub = (action) => () => {
    console.log(`[SalesDetail] ${action} for sale`, sale.id, sale.code);
  };

  const handleResendEmail = async () => {
    if (!onResendEmail || busy) return;
    setBusy('resend');
    try {
      await onResendEmail(sale);
    } catch (e) {
      console.error('[SalesDetail] Resend email failed', e);
    } finally {
      setBusy(null);
    }
  };

  // Open the inline attach form for a role (falls back to the console stub if
  // no handler is wired).
  const openAttach = (role) => () => {
    if (!onAttachClient) { stub(`Attach ${role}`)(); return; }
    setAttachRole(role);
    setAttachName('');
    setAttachEmail('');
  };

  const submitAttach = async () => {
    if (!onAttachClient || attaching || (!attachName && !attachEmail)) return;
    setAttaching(true);
    try {
      await onAttachClient(sale, attachRole, { name: attachName || null, email: attachEmail || null });
      setAttachRole(null);
    } catch (e) {
      console.error('[SalesDetail] Attach client failed', e);
    } finally {
      setAttaching(false);
    }
  };

  // Inline name/email capture shown when an "Attach to client" CTA is clicked.
  const renderAttachForm = (role) => (
    <div className="v2-sd-attach-form">
      <input
        type="text"
        placeholder="Name"
        value={attachName}
        onChange={(e) => setAttachName(e.target.value)}
      />
      <input
        type="email"
        placeholder="Email"
        value={attachEmail}
        onChange={(e) => setAttachEmail(e.target.value)}
      />
      <div className="v2-sd-attach-form-actions">
        <button type="button" className="sp-btn-secondary" onClick={() => setAttachRole(null)} disabled={attaching}>
          Cancel
        </button>
        <button type="button" className="sp-btn-primary" onClick={submitAttach} disabled={attaching || (!attachName && !attachEmail)}>
          {attaching ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );

  const titleText = unclaimed
    ? 'Unclaimed sale'
    : (sale.recipientName || sale.buyerName || 'Sale');

  return (
    <SlidePanel
      title={titleText}
      onClose={onClose}
      width={520}
      className="voucher-v2 v2-sales-detail"
      headerRight={
        <span
          className="v2-sales-detail-status-pill"
          style={{ background: status.bg, color: status.color }}
        >
          {status.label}
        </span>
      }
    >
      {/* Card hero — the rendered design, so staff can see the card itself.
          For salons that issue physical cards, this is the reference they
          look at while writing the code (below) onto a physical card. Only
          shown when the sale has a chosen design. */}
      {design && (
        <div className="v2-sd-card">
          <VoucherCard
            voucher={buildPreviewVoucher(design, sale.faceValue)}
            aesthetic={design.aesthetic}
            salonName={salonName}
            currency={currency}
            showPromo={false}
          />
        </div>
      )}

      {/* Code block — large monospace, copy + print buttons. The single
          highest-value piece of info for staff reading a code out to a
          customer or writing it onto a physical card. */}
      <div className="v2-sd-code-block">
        <span className="beu-section-label">Voucher code</span>
        <div className="v2-sd-code">{sale.code}</div>
        <div className="v2-sd-code-actions">
          <button type="button" className="v2-sd-code-btn" onClick={copyCode}>
            <CopyIcon /> {copied ? 'Copied' : 'Copy code'}
          </button>
          <button type="button" className="v2-sd-code-btn" onClick={printVoucher}>
            <PrintIcon /> Print receipt
          </button>
        </div>
      </div>

      {/* Buyer — falls back to an Attach CTA when nothing's on file. This
          is the cash-walk-out workflow: salon staff didn't capture an
          email at POS time, and now needs to bind a client retroactively. */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Buyer</p>
        {(sale.buyerName || sale.buyerEmail) ? (
          <div className="v2-sd-person">
            <div className="v2-sd-person-name">{sale.buyerName || 'None'}</div>
            {sale.buyerEmail && <div className="v2-sd-person-meta">{sale.buyerEmail}</div>}
          </div>
        ) : attachRole === 'buyer' ? (
          renderAttachForm('buyer')
        ) : (
          <button type="button" className="v2-sd-attach-cta" onClick={openAttach('buyer')}>
            <PlusIcon /> Attach to client
          </button>
        )}
      </div>

      {/* Recipient — only when it's a gift (distinct from buyer). For
          unclaimed sales we also surface an Attach CTA so staff can
          record both buyer + recipient if the customer comes back. */}
      {hasGift && (
        <div className="v2-pp-section">
          <p className="beu-section-label">Recipient</p>
          <div className="v2-sd-person">
            <div className="v2-sd-person-name">{sale.recipientName || 'None'}</div>
            {sale.recipientEmail && <div className="v2-sd-person-meta">{sale.recipientEmail}</div>}
          </div>
          {sale.message && (
            <blockquote className="v2-sd-message">{sale.message}</blockquote>
          )}
        </div>
      )}
      {!hasGift && unclaimed && (
        <div className="v2-pp-section">
          <p className="beu-section-label">Recipient</p>
          {attachRole === 'recipient' ? (
            renderAttachForm('recipient')
          ) : (
            <button type="button" className="v2-sd-attach-cta" onClick={openAttach('recipient')}>
              <PlusIcon /> Attach to client
            </button>
          )}
        </div>
      )}

      {/* Voucher product + denomination details */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Voucher</p>
        <div className="v2-sd-rows">
          {voucher && (
            <div className="v2-sd-row">
              <span>Product</span>
              <strong>{voucher.name}</strong>
            </div>
          )}
          {design && (
            <div className="v2-sd-row">
              <span>Design</span>
              <strong>{(design.label || '').replace(/^[^·]+·\s*/, '') || sale.designId}</strong>
            </div>
          )}
          <div className="v2-sd-row">
            <span>Face value</span>
            <strong>{currency}{sale.faceValue}</strong>
          </div>
          {promoActive && (
            <div className="v2-sd-row">
              <span>Paid</span>
              <strong>
                {currency}{sale.paidAmount}{' '}
                <span className="v2-sd-row-meta">promo</span>
              </strong>
            </div>
          )}
          <div className="v2-sd-row">
            <span>Redeemed</span>
            <strong>{currency}{sale.redeemedAmount} / {currency}{sale.faceValue}</strong>
          </div>
        </div>
      </div>

      {/* Sale meta (channel, dates) */}
      <div className="v2-pp-section">
        <p className="beu-section-label">Sale</p>
        <div className="v2-sd-rows">
          <div className="v2-sd-row">
            <span>Channel</span>
            <strong>{sale.channel === 'pos' ? 'POS' : 'Online'}</strong>
          </div>
          <div className="v2-sd-row">
            <span>Sold</span>
            <strong>{fmtDateTime(sale.soldAt)}</strong>
          </div>
          <div className="v2-sd-row">
            <span>Expires</span>
            <strong>{fmtDate(sale.expiresAt)}</strong>
          </div>
        </div>
      </div>

      {/* Actions — stubs to console.log until backend wires up. The
          email-resend is disabled when there's no email to send to. */}
      <div className="v2-pp-section v2-sd-actions">
        <button
          type="button"
          className="sp-btn-secondary"
          onClick={handleResendEmail}
          disabled={(!sale.buyerEmail && !sale.recipientEmail) || busy === 'resend'}
        >
          {busy === 'resend' ? 'Sending…' : 'Resend voucher email'}
        </button>
        <button type="button" className="sp-btn-secondary" onClick={printVoucher}>
          Download PDF
        </button>
        {/* No "Mark redeemed" action — redemption happens through the system
            (POS / checkout), not by manually flagging a sale here. Refunds
            are likewise handled outside Beu for now. */}
      </div>

      {/* Print-only sheet — offscreen on screen media; the @media print rules
          in GiftCardsV2.css hide everything else and bring this on-page. */}
      {createPortal(
        <div className="v2-sd-print" aria-hidden>
          <div className="v2-sd-print-head">
            <div className="v2-sd-print-salon">{salonName}</div>
            <div className="v2-sd-print-title">Gift card</div>
          </div>
          {design && (
            <div className="v2-sd-print-card">
              <VoucherCard
                voucher={buildPreviewVoucher(design, sale.faceValue)}
                aesthetic={design.aesthetic}
                salonName={salonName}
                currency={currency}
                showPromo={false}
              />
            </div>
          )}
          <div className="v2-sd-print-code">
            <span>Voucher code</span>
            <strong>{sale.code}</strong>
          </div>
          <table className="v2-sd-print-table">
            <tbody>
              <tr><td>Value</td><td>{currency}{sale.faceValue}</td></tr>
              <tr><td>Paid</td><td>{currency}{sale.paidAmount}</td></tr>
              {sale.redeemedAmount > 0 && (
                <tr><td>Balance remaining</td><td>{currency}{Math.max(0, sale.faceValue - sale.redeemedAmount)}</td></tr>
              )}
              {(sale.buyerName || sale.buyerEmail) && (
                <tr><td>Buyer</td><td>{sale.buyerName || ''}{sale.buyerName && sale.buyerEmail ? ' · ' : ''}{sale.buyerEmail || ''}</td></tr>
              )}
              {(sale.recipientName || sale.recipientEmail) && (
                <tr><td>Recipient</td><td>{sale.recipientName || ''}{sale.recipientName && sale.recipientEmail ? ' · ' : ''}{sale.recipientEmail || ''}</td></tr>
              )}
              {sale.message && (
                <tr><td>Message</td><td>&ldquo;{sale.message}&rdquo;</td></tr>
              )}
              <tr><td>Issued</td><td>{fmtDate(sale.soldAt)}</td></tr>
              <tr><td>Expires</td><td>{sale.expiresAt ? fmtDate(sale.expiresAt) : 'No expiry'}</td></tr>
            </tbody>
          </table>
          <div className="v2-sd-print-foot">
            Redeem in salon or when booking online. Powered by Beu.
          </div>
        </div>,
        document.body
      )}
    </SlidePanel>
  );
};

export default SalesDetailPanel;
